import { updateAndSortFields } from '../common/output-layout-utils';
import { AnyRecord, OutputLayoutField } from '@ssar-app/domain';
import { Box, Button, HStack, Icon, Input } from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { AiOutlineHolder } from 'react-icons/ai/index.js';
import { UniqueIdentifier } from '@dnd-kit/core';

export const merge = (
  a: OutputLayoutField[],
  b: OutputLayoutField[],
  i = 0,
) => {
  const aa = [...a];
  return [...a.slice(0, i), ...b, ...aa.slice(i, aa.length)];
};

export const getTextFilterProps = (
  title: string,
  searchInputRef: React.RefObject<HTMLInputElement>,
  type: React.HTMLInputTypeAttribute = 'text',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filterDropdown: (props: any) => (
      <Box p={2}>
        <Input
          type={type}
          ref={searchInputRef}
          placeholder={`Search ${title}`}
          value={props.selectedKeys[0]}
          onChange={(e) =>
            props.setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onKeyDown={(e) => {
            if (e.code === 'Enter') {
              props.confirm();
            }
          }}
          size='sm'
          mb={2}
          w={48}
          spellCheck={false}
          {...(type === 'number' ? { min: 1 } : {})}
        />
        <HStack>
          <Button
            colorScheme='blue'
            onClick={() => props.confirm()}
            size='sm'
            w={24}
          >
            Search
          </Button>
          <Button
            colorScheme='gray'
            onClick={() => {
              props.clearFilters();
              props.confirm();
            }}
            size='sm'
            w={24}
          >
            Reset
          </Button>
        </HStack>
      </Box>
    ),
    onFilterDropdownVisibleChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInputRef.current?.select());
      }
    },
  };
};

export const DragHandle = (props: AnyRecord) => {
  const { listeners, attributes, setActivatorNodeRef } = useSortable({
    id: props?.id,
  });
  return (
    <Button
      ref={setActivatorNodeRef}
      color={props.active ? 'primary' : 'unset'}
      cursor='move'
      size='xs'
      {...listeners}
      {...attributes}
    >
      <Icon as={AiOutlineHolder} fontSize='lg' />
    </Button>
  );
};

export const onSortEnd = (
  rows: OutputLayoutField[],
  selectedFields: string[],
  oldInd: UniqueIdentifier,
  newInd?: UniqueIdentifier,
) => {
  if (!newInd) {
    return;
  }
  const oldIndex =
    (rows.find((row) => row.meta?.id === oldInd)?.meta?.index || 1) - 1;
  const newIndex =
    (rows.find((row) => row.meta?.id === newInd)?.meta?.index || 1) - 1;
  let tempRows = Array.from(rows);

  if (oldIndex !== newIndex) {
    if (!selectedFields.length) {
      const movingItem = tempRows[oldIndex];
      tempRows.splice(oldIndex, 1);
      tempRows = merge(tempRows, [movingItem], newIndex);
    } else {
      const filteredItems: OutputLayoutField[] = [];
      selectedFields.forEach((id) => {
        filteredItems.push(
          tempRows.find((row) => row.meta?.id === id) as OutputLayoutField,
        );
      });
      const newData: OutputLayoutField[] = [];
      tempRows.forEach((row) => {
        if (!selectedFields.includes(row.meta?.id)) {
          newData.push(row);
        }
      });
      tempRows = [...newData];
      tempRows = merge(tempRows, filteredItems, newIndex);
    }
    tempRows = Array.from(new Set(tempRows).values()).filter((row) => !!row);
    tempRows = updateAndSortFields(tempRows);
    return tempRows;
  }
};

export const insertCopiedAndRemoveCutRows = (
  rows: OutputLayoutField[],
  copiedRows: OutputLayoutField[],
  index: number,
) => {
  return updateAndSortFields(
    [...rows.slice(0, index), ...copiedRows, ...rows.slice(index)].filter(
      (row) => !row.meta?.wasCut,
    ),
  );
};

export const getErrorMessage = (
  record: OutputLayoutField,
): { field?: keyof OutputLayoutField; errorMsg: string } => {
  if (!record.headerName) {
    return {
      field: 'headerName',
      errorMsg: 'Header name is a required field.',
    };
  } else if (!record.fieldLength || record.fieldLength < 1) {
    return {
      field: 'fieldLength',
      errorMsg: 'Field Length must be greater than or equal to 1.',
    };
  } else if (record.fieldLength < (record.constantValue || '').length) {
    return {
      field: 'fieldLength',
      errorMsg:
        'Field length should be greater than or equal to length of constant value.',
    };
  }
  return { errorMsg: '' };
};
